import axios from './axios-instance';
import User from "../models/User";
import ApiResponse from "../models/ApiResponse";
import LoginRequest from "../models/LoginRequest";
import CognitoSession from "../models/CognitoSession";
import ResetPasswordRequest from "../models/ResetPasswordRequest";
import ChangePasswordRequest from "../models/ChangePasswordRequest";
import {AxiosInstance} from "axios";
import axiosRetry from "axios-retry";

export const refreshTokens = async (
    refreshToken: string,
    user: User
): Promise<ApiResponse<CognitoSession>> => {

    const refreshAxios: AxiosInstance = axios.create({
        baseURL: "/api",
        headers: {
            "Content-Type": "application/json",
        },
    });

    axiosRetry(refreshAxios, {
        retries: 3,
        retryDelay: (retryCount, error) => {
            const delay = axiosRetry.exponentialDelay(retryCount);
            console.warn(`[refreshTokens] Retry attempt ${retryCount} in ${delay}ms`, {
                message: error.message,
                code: error.code
            });
            return delay;
        },
        retryCondition: (error) => {
            const shouldRetry = axiosRetry.isNetworkError(error) && !error.response;
            if (shouldRetry) {
                console.warn("[refreshTokens] Network error detected. Will retry.");
            }
            return shouldRetry;
        },
        onRetry: (retryCount, error, requestConfig) => {
            console.warn(`[refreshTokens] Executing retry #${retryCount} for ${requestConfig.url}`, {
                    message: error.message,
                    code: error.code
            });
        }
    });

    try {
        const response = await refreshAxios.post("/auth/refresh", {
            refreshToken,
            user
        });
        return {
            data: response.data,
            status: response.status
        };
    } catch (err: any) {
        console.error("Failed to refresh tokens:", err.response?.data || err.message);
        return {
            data: null,
            status: err.status || err.response?.status || 0,
        };
    }
};

export const signup = async (user: User): Promise<ApiResponse<String>> => {
    try {
        const response = await axios.post('/auth/signup', user);
        return {
            data: response.data,
            status: response.status,
        };
    } catch (err: any) {
        console.error('Signup failed:', err.response?.data || err.message)
        return {
            data:  err.response?.data || err.message,
            status: err.status
        };
    }
};

export const confirmEmail = async (email: String, code: String): Promise<ApiResponse<String>> => {
    try {
        const response = await axios.post('/auth/confirmEmail', null, {
            params: {
                email: email,
                code: code
            }
        });
        return {
            data: response.data,
            status: response.status
        };

    } catch (err: any) {
        console.error('Email confirmation failed:', err.response?.data || err.message)
        return {
            data:  err.response?.data || err.message,
            status: err.status
        };
    }
}

export const resendCode = async (email: String): Promise<ApiResponse<String>> => {
    try {
        const response = await axios.post('/auth/resendCode', null, {
            params: {
                email: email
            }
        });
        return {
            data: response.data,
            status: response.status
        };

    } catch (err: any) {
        console.error('Failed to resend confirmation code:', err.response?.data || err.message)
        return {
            data:  err.response?.data || err.message,
            status: err.status
        };
    }
}

export const login = async (loginRequest: LoginRequest): Promise<ApiResponse<CognitoSession>> => {
    try {
        const response = await axios.post('/auth/login', loginRequest)
        return {
            data: response.data as CognitoSession,
            status: response.status
        };
    } catch (err: any) {
        console.error('Failed to login:', err.response?.data || err.message)
        return {
            data: null,
            status: err.status
        };
    }
}
export const logout = async (email: String): Promise<ApiResponse<String>> => {
    try {
        const response = await axios.post('/auth/logout', null, {
            params: {
                email: email
            }
        });
        return {
            data: response.data,
            status: response.status
        };

    } catch (err: any) {
        console.error('Failed to logout:', err.response?.data || err.message)
        return {
            data:  err.response?.data || err.message,
            status: err.status
        };
    }
}

export const getUserById = async (id: number): Promise<ApiResponse<User>> => {
    try {
        const response = await axios.get('/user/' + id);
        return {
            data: response.data as User,
            status: response.status
        };
    } catch (err: any) {
        console.error('Failed to get user: ', err.response?.data || err.message);
        return {
            data: null,
            status: err.status
        }
    }
}

export const updateUser = async (user: User): Promise<ApiResponse<User>> => {
    try {
        const response = await axios.post('/user/update', user);
        return {
            data: response.data as User,
            status: response.status
        };
    } catch (err: any) {
        console.error('Failed to update user: ', err.response?.data || err.message);
        return {
            data: null,
            status: err.status
        }
    }
}

export const deleteUser = async (id: number): Promise<{status: number}> => {
    try {
        const response = await axios.delete('/user/delete/' + id);
        return {status: response.status};
    } catch (err: any) {
        console.error('Failed to delete user: ', err.response?.data || err.message);
        return {status: err.status};
    }
}

export const requestPasswordReset = async (email: string): Promise<{status: number}> => {
    try {
        const response = await axios.post('/auth/forgot-password', null, {
            params: {
                email: email
            }
        });
        return {status: response.status};
    } catch (err: any) {
        console.error('Error sending forgot password request', err.response?.data || err.message);
        return {status: err.status};
    }
}

export const confirmPasswordReset = async (resetPasswordRequest: ResetPasswordRequest): Promise<ApiResponse<string>> => {
    try {
        const response = await axios.post('/auth/password-reset', resetPasswordRequest);
        return response as ApiResponse<string>;
    } catch (err: any) {
        console.error('Error confirming password reset', err.response?.data || err.message);
        return {
            status: err.status,
            data: err.response?.data || "Unable to reset password."
        };
    }
}

export const changePassword = async (changePasswordRequest: ChangePasswordRequest): Promise<{status: number}> => {
    try {
        const response = await axios.post('/auth/settings/change-password', changePasswordRequest);
        return {status: response.status}
    } catch (err: any) {
        console.error('Error changing password', err.response?.data || err.message);
        return {
            status: err.status
        }
    }
}

